import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { expVal } from '@atlaskit/tmp-editor-statsig/expVal';
import { getPreferences, PreferenceCategory } from '@atlassian/browser-storage-controls';
import type { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import type { AssistanceServiceInputParam } from '@atlassian/conversation-assistant-service/services/assistance-service/main';
import { useProfileController } from '@atlassian/conversation-assistant-store/controllers/profile';

import {
	type GhostIconConnector,
	type GhostIconConnectorsFetchOutcome,
	useGhostIconConnectors,
} from '../../../../../common/hooks/useGhostIconConnectors';
import { DISMISS_STATE_KIND } from '../../../../../common/services/ghost-icons-dismiss-state/DISMISS_STATE_KIND';
import { type DismissStateTransition } from '../../../../../common/services/ghost-icons-dismiss-state/DismissStateTransition';
import { getGhostIconsRenderMode } from '../../../../../common/services/ghost-icons-dismiss-state/getGhostIconsRenderMode';
import { getLocalState } from '../../../../../common/services/ghost-icons-dismiss-state/getLocalState';
import { GHOST_ICONS_RENDER_MODE } from '../../../../../common/services/ghost-icons-dismiss-state/GHOST_ICONS_RENDER_MODE';
import { recordDismissed } from '../../../../../common/services/ghost-icons-dismiss-state/recordDismissed';
import { recordFirstView } from '../../../../../common/services/ghost-icons-dismiss-state/recordFirstView';
import { recordSourcesClicked } from '../../../../../common/services/ghost-icons-dismiss-state/recordSourcesClicked';

// Reasons why ghost icons did not render for a user who otherwise could have
// seen them. Drives the `reason` attribute on the `ghost_icons_not_rendered`
// track event and closes the gap between Statsig exposures and the
// `ghost_icons_rendered` track event.
//
// Grouped into two cohorts:
//   "hidden_*" — render mode is HIDDEN due to dismiss state. These users
//     never fire an experiment exposure (by design, so they do not dilute the
//     experiment), but they are still product-relevant: they tell us how many
//     users actively dismissed vs. let the safety-net window expire.
//   "no_site_id" / "no_eligible_connectors" / "fetch_failed" — user is in
//     the experiment (exposure fired) but the connector data path did not
//     produce a renderable set. These reasons close the
//     exposure-minus-rendered funnel gap.
//
// We do not emit a reason for "experiment_off" — that is the experiment
// baseline and would drown out the more actionable reasons above.
type NotRenderedReason =
	| 'hidden_dismissed'
	| 'hidden_seen_expired'
	| 'hidden_timer_expired'
	| 'no_functional_consent_with_connected_apps'
	| 'no_site_id'
	| 'no_eligible_connectors'
	| 'fetch_failed';

type DismissStateKind = (typeof DISMISS_STATE_KIND)[keyof typeof DISMISS_STATE_KIND] | null;

// Lazy localStorage read used at analytics-fire time so the persisted dismiss
// kind can be attached as an attribute without leaking it into render-time
// memoization (the state machine can mutate during the session via
// recordFirstView / recordSourcesClicked / recordDismissed).
const readDismissStateKind = (aaid: string | null | undefined): DismissStateKind => {
	if (!aaid) {
		return null;
	}
	return getLocalState(aaid)?.kind ?? null;
};

type GhostIconsPillState = {
	ghostConnectors: GhostIconConnector[];
	showGhostIcons: boolean;
	// Ghost icons are eligible to show but their connector data is still loading.
	// Consumers use this to suppress the overflow count while we wait, so it does
	// not flash in and then get replaced by the ghost icon wave.
	isGhostIconsPending: boolean;
	shouldAnimate: boolean;
	oauthSource: string;
	handleSourcesPillClick: () => void;
	handleGhostConnectClick: (source: string) => void;
	// Pass this through to `useGhostRenderCoordination`'s `onRenderBlocked`
	// option so the `ghost_icons_render_blocked` operational event stays in
	// the same analytics surface as the other ghost-icons events. The hook
	// itself doesn't own coordination state, so the wiring has to happen at
	// the caller — but the event payload construction lives here.
	reportRenderBlocked: (detail: { reason: string; pendingSources: string[] }) => void;
};

type UseGhostIconsPillStateArgs = {
	assistanceServiceParams: AssistanceServiceInputParam['assistanceServiceParams'];
	contentContext: string;
	connectedThirdPartyAppCount: number;
	sendAnalyticsEvent: ReturnType<typeof useAnalytics>['sendAnalyticsEvent'];
};

export const useGhostIconsPillState = ({
	assistanceServiceParams,
	contentContext,
	connectedThirdPartyAppCount,
	sendAnalyticsEvent,
}: UseGhostIconsPillStateArgs): GhostIconsPillState => {
	const { aaid } = useProfileController();
	const [hasFunctionalConsent, setHasFunctionalConsent] = useState(false);
	const hasConnectedThirdPartyApps = connectedThirdPartyAppCount > 0;
	const shouldUseDismissState = hasFunctionalConsent && Boolean(aaid);

	const renderMode = useMemo(() => {
		if (!aaid) {
			return GHOST_ICONS_RENDER_MODE.HIDDEN;
		}
		if (!hasFunctionalConsent) {
			return GHOST_ICONS_RENDER_MODE.STATIC;
		}
		return getGhostIconsRenderMode(aaid);
	}, [aaid, hasFunctionalConsent]);

	// Single, consolidated experiment check. Every cheap exclusion that does
	// not require fetching connector data runs first, so we only fire an
	// exposure for users who could plausibly see ghost icons (anything else
	// would dilute the experiment results). The experiment value must always
	// be the last term in the boolean chain.
	//
	// This value also gates the TAP fetch in `useGhostIconConnectors` below,
	// so consumers outside the experiment never pay the network cost — and we
	// avoid making a second `expVal` call to gate the fetch separately.
	const isInGhostIconsExperiment = useMemo(() => {
		if (!aaid) {
			return false;
		}
		if (shouldUseDismissState && renderMode === GHOST_ICONS_RENDER_MODE.HIDDEN) {
			return false;
		}
		return expVal('confluence_3p_in_cwr_ghost_icons', 'isEnabled', false);
	}, [aaid, renderMode, shouldUseDismissState]);

	useEffect(() => {
		// Only fetch consent when the user is authenticated (ConsentHub requires
		// a session — will always 404 without one) AND the experiment is active.
		// This references the single consolidated expVal check above, so we
		// avoid firing a second exposure or diluting experiment results.
		if (!aaid || !isInGhostIconsExperiment) {
			return;
		}
		let cancelled = false;
		void getPreferences()
			.then((prefs) => {
				if (!cancelled) {
					setHasFunctionalConsent(Boolean(prefs?.[PreferenceCategory.Functional]));
				}
			})
			.catch(() => {
				if (!cancelled) {
					setHasFunctionalConsent(false);
				}
			});
		return () => {
			cancelled = true;
		};
	}, [aaid, isInGhostIconsExperiment]);

	// Captured from the connectors hook's onFetchComplete callback so the
	// `not_rendered` reason can distinguish "TAP succeeded but returned an
	// empty list" from "TAP failed and we fell back to empty". Without this
	// the two are indistinguishable to the analytics consumer.
	const [lastFetchResult, setLastFetchResult] = useState<{
		outcome: GhostIconConnectorsFetchOutcome;
		durationMs: number;
	} | null>(null);

	// Invalidate the cached fetch result whenever the site changes so a stale
	// outcome from the previous site cannot be attached to the new site's
	// `ghost_icons_not_rendered` event. The connectors hook already reports
	// `loaded=false` during the refetch (so the reason is suppressed as
	// transient until the new fetch settles), but resetting here keeps the
	// invariant local instead of relying on cross-hook timing.
	useEffect(() => {
		setLastFetchResult(null);
	}, [assistanceServiceParams.siteId]);

	const handleConnectorsFetchComplete = useCallback(
		(result: { outcome: GhostIconConnectorsFetchOutcome; durationMs: number }) => {
			setLastFetchResult(result);
			// Operational (not track) so this does not show up in product
			// analytics dashboards, but lets SRE alert on the failure /
			// timeout rate and tune TAP_TRAITS_FETCH_TIMEOUT_MS against
			// real p95s instead of guesses.
			sendAnalyticsEvent({
				eventType: 'operational',
				action: 'ghost_icons_connectors_fetch_completed',
				actionSubject: 'ghostIcons',
				attributes: {
					outcome: result.outcome,
					durationMs: Math.round(result.durationMs),
				},
			});
		},
		[sendAnalyticsEvent],
	);

	const { connectors: ghostConnectors, loaded: ghostConnectorsLoaded } = useGhostIconConnectors(
		assistanceServiceParams.siteId,
		isInGhostIconsExperiment,
		{ onFetchComplete: handleConnectorsFetchComplete },
	);

	const showGhostIcons =
		isInGhostIconsExperiment &&
		ghostConnectorsLoaded &&
		ghostConnectors.length > 0 &&
		(hasFunctionalConsent || !hasConnectedThirdPartyApps);

	// True while ghost icons could still appear but their connector data has not
	// loaded yet. Distinct from showGhostIcons (which requires loaded data) so the
	// pill can hide the overflow count during the fetch and avoid a flash.
	const isGhostIconsPending = isInGhostIconsExperiment && !ghostConnectorsLoaded;

	const shouldAnimate =
		showGhostIcons && hasFunctionalConsent && renderMode === GHOST_ICONS_RENDER_MODE.ANIMATED;

	// Helper that fires the dismiss-state transition event. Hoisted so all three
	// state-changing call sites (recordFirstView, recordSourcesClicked,
	// recordDismissed) use the exact same shape. Noop results (idempotent or
	// safety-net branches that did not rewrite storage — e.g. `recordFirstView`
	// being called a second time after a re-render, or `recordSourcesClicked`
	// hitting the expired-SEEN branch) are intentionally dropped: the event
	// name promises an actual transition, and re-firing on every effect
	// re-run would inflate the dismiss-state funnel with phantom moves.
	const fireDismissStateTransition = useCallback(
		(
			trigger: 'first_view' | 'sources_clicked' | 'connect_clicked',
			transitionResult: DismissStateTransition,
		) => {
			if (transitionResult.noop) {
				return;
			}
			sendAnalyticsEvent({
				eventType: 'track',
				action: 'ghost_icons_dismiss_state_transitioned',
				actionSubject: 'ghostIcons',
				attributes: {
					trigger,
					from: transitionResult.from,
					to: transitionResult.to,
					surface: contentContext,
				},
			});
		},
		[sendAnalyticsEvent, contentContext],
	);

	useEffect(() => {
		if (!showGhostIcons || !shouldUseDismissState || !aaid) {
			return;
		}
		// Chain on the recordX promise so we observe the actual transition the
		// service computed. The fire helper drops noop results, so re-running
		// this effect (e.g. on a new `sendAnalyticsEvent` identity) after the
		// first view has already been recorded is safe — `recordFirstView`
		// returns `{noop: true}` and no event is emitted.
		void recordFirstView(aaid).then((transitionResult) => {
			fireDismissStateTransition('first_view', transitionResult);
		});
	}, [showGhostIcons, shouldUseDismissState, aaid, fireDismissStateTransition]);

	// `ghost_icons_rendered` dedupe: fire exactly once per "showGhostIcons
	// transitions false → true" window. Previously this fired any time
	// `ghostConnectors` re-referenced (every connector store re-emit) or
	// `sendAnalyticsEvent` identity churned, inflating impression counts and
	// distorting the numerator of the conversion funnel.
	const renderedFiredRef = useRef(false);
	useEffect(() => {
		if (!showGhostIcons) {
			renderedFiredRef.current = false;
			return;
		}
		if (renderedFiredRef.current) {
			return;
		}
		renderedFiredRef.current = true;
		sendAnalyticsEvent({
			eventType: 'track',
			action: 'ghost_icons_rendered',
			actionSubject: 'ghostIcons',
			attributes: {
				sources: ghostConnectors.map((connector) => connector.source),
				surface: contentContext,
				animated: shouldAnimate,
				// New attributes for richer funnel slicing — let dashboards split
				// rendered impressions by which dismiss-state cohort the user
				// was in (SEEN first-time animated vs TIMER_RUNNING static
				// follow-up) without needing a separate event.
				renderMode,
				dismissStateKind: shouldUseDismissState ? readDismissStateKind(aaid) : null,
				connectorCount: ghostConnectors.length,
			},
		});
		// Intentionally only re-runs when the visibility predicate flips. The
		// other values are read from the current closure at fire time, which
		// matches the impression we are recording (the wave the user is
		// looking at right now).
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [showGhostIcons]);

	// Inferred reason the user did not see ghost icons. Returns null in two
	// cases that we explicitly do NOT want to report:
	//   1. transient — fetch is still in flight, so any "no connectors"
	//      conclusion would be a false signal.
	//   2. user is out of the experiment AND not blocked by dismiss state —
	//      this is the experiment baseline and reporting it would flood the
	//      track event with the population we are not trying to convert.
	const notRenderedReason = useMemo<NotRenderedReason | null>(() => {
		if (!aaid || showGhostIcons) {
			return null;
		}

		// Hidden render mode short-circuits the experiment, so the dismiss
		// state is the only signal available. SEEN / TIMER_RUNNING + HIDDEN
		// implies the safety-net window expired (otherwise renderMode would
		// be STATIC); fold that knowledge into the reason value.
		if (!hasFunctionalConsent && hasConnectedThirdPartyApps) {
			return 'no_functional_consent_with_connected_apps';
		}

		if (shouldUseDismissState && renderMode === GHOST_ICONS_RENDER_MODE.HIDDEN) {
			const kind = readDismissStateKind(aaid);
			if (kind === DISMISS_STATE_KIND.DISMISSED) {
				return 'hidden_dismissed';
			}
			if (kind === DISMISS_STATE_KIND.SEEN) {
				return 'hidden_seen_expired';
			}
			if (kind === DISMISS_STATE_KIND.TIMER_RUNNING) {
				return 'hidden_timer_expired';
			}
			// Defensive: null state always maps to ANIMATED in the service,
			// so we should never get HIDDEN with no kind. Skip rather than
			// emit a misleading reason.
			return null;
		}

		// From here, renderMode is not HIDDEN. Anything else implies we are
		// either in the experiment funnel (so we want the diagnostics) or out
		// of it entirely (which we skip per the rationale above).
		if (!isInGhostIconsExperiment) {
			return null;
		}

		if (!assistanceServiceParams.siteId) {
			return 'no_site_id';
		}

		if (!ghostConnectorsLoaded) {
			// Transient — wait for the fetch to settle before drawing any
			// conclusion. notRenderedReason will recompute when `loaded`
			// flips, at which point we will either show icons or pick a
			// concrete reason below.
			return null;
		}

		if (lastFetchResult?.outcome === 'failure' || lastFetchResult?.outcome === 'timeout') {
			return 'fetch_failed';
		}

		if (ghostConnectors.length === 0) {
			// Only classify as "no eligible connectors" once a fetch has
			// actually settled. Without this guard, any render where the
			// connectors hook reports `loaded=true` with an empty list before
			// a real fetch completes (e.g. the pre-fetch transient when
			// `enabled` flips on) would be reported as a TAP-empty outcome,
			// which would also incorrectly co-fire with `ghost_icons_rendered`
			// once the fetch settles with results.
			if (!lastFetchResult || lastFetchResult.outcome !== 'success') {
				return null;
			}
			return 'no_eligible_connectors';
		}

		return null;
	}, [
		aaid,
		showGhostIcons,
		renderMode,
		hasFunctionalConsent,
		hasConnectedThirdPartyApps,
		shouldUseDismissState,
		isInGhostIconsExperiment,
		assistanceServiceParams.siteId,
		ghostConnectorsLoaded,
		ghostConnectors.length,
		lastFetchResult,
	]);

	const lastNotRenderedReasonRef = useRef<NotRenderedReason | null>(null);
	useEffect(() => {
		if (!notRenderedReason) {
			// Resetting on transitions back to null lets a later regression
			// (e.g. site change → refetch → empty again) re-fire the same
			// reason instead of being silently suppressed by the dedupe latch.
			lastNotRenderedReasonRef.current = null;
			return;
		}
		if (lastNotRenderedReasonRef.current === notRenderedReason) {
			return;
		}
		lastNotRenderedReasonRef.current = notRenderedReason;
		sendAnalyticsEvent({
			eventType: 'track',
			action: 'ghost_icons_not_rendered',
			actionSubject: 'ghostIcons',
			attributes: {
				surface: contentContext,
				reason: notRenderedReason,
				renderMode,
				dismissStateKind: shouldUseDismissState ? readDismissStateKind(aaid) : null,
				// Attach fetch context only when relevant — keeps the payload
				// small for the dismiss-state branches that never touched TAP.
				fetchOutcome:
					notRenderedReason === 'fetch_failed' || notRenderedReason === 'no_eligible_connectors'
						? (lastFetchResult?.outcome ?? null)
						: null,
				fetchDurationMs:
					notRenderedReason === 'fetch_failed' || notRenderedReason === 'no_eligible_connectors'
						? lastFetchResult
							? Math.round(lastFetchResult.durationMs)
							: null
						: null,
			},
		});
	}, [
		notRenderedReason,
		sendAnalyticsEvent,
		contentContext,
		renderMode,
		shouldUseDismissState,
		aaid,
		lastFetchResult,
	]);

	const handleSourcesPillClick = useCallback(() => {
		if (!showGhostIcons || !aaid) {
			return;
		}
		const timerAlreadyStarted =
			shouldUseDismissState && getLocalState(aaid)?.kind === DISMISS_STATE_KIND.TIMER_RUNNING;
		if (shouldUseDismissState) {
			void recordSourcesClicked(aaid).then((transitionResult) => {
				fireDismissStateTransition('sources_clicked', transitionResult);
			});
		}
		sendAnalyticsEvent({
			eventType: 'track',
			action: 'ghost_icons_sources_pill_clicked',
			actionSubject: 'ghostIcons',
			attributes: {
				sources: ghostConnectors.map((connector) => connector.source),
				surface: contentContext,
				timerAlreadyStarted,
			},
		});
	}, [
		showGhostIcons,
		aaid,
		sendAnalyticsEvent,
		ghostConnectors,
		contentContext,
		fireDismissStateTransition,
		shouldUseDismissState,
	]);

	const handleGhostConnectClick = useCallback(
		(source: string) => {
			if (!showGhostIcons || !aaid) {
				return;
			}
			if (shouldUseDismissState) {
				void recordDismissed(aaid).then((transitionResult) => {
					fireDismissStateTransition('connect_clicked', transitionResult);
				});
			}
			sendAnalyticsEvent({
				eventType: 'track',
				action: 'ghost_icons_connect_clicked',
				actionSubject: 'ghostIcons',
				attributes: {
					source,
					surface: contentContext,
				},
			});
		},
		[
			showGhostIcons,
			aaid,
			sendAnalyticsEvent,
			contentContext,
			fireDismissStateTransition,
			shouldUseDismissState,
		],
	);

	// Wraps an operational `ghost_icons_render_blocked` event so the caller
	// (FilteredAppsTagNew) can pass this straight into `useGhostRenderCoordination`'s
	// `onRenderBlocked` option without re-implementing the event shape. Kept
	// here so every ghost-icons analytics event is constructed from the same
	// hook and the event vocabulary is easy to audit.
	const reportRenderBlocked = useCallback(
		(detail: { reason: string; pendingSources: string[] }) => {
			sendAnalyticsEvent({
				eventType: 'operational',
				action: 'ghost_icons_render_blocked',
				actionSubject: 'ghostIcons',
				attributes: {
					reason: detail.reason,
					pendingSources: detail.pendingSources,
					surface: contentContext,
				},
			});
		},
		[sendAnalyticsEvent, contentContext],
	);

	return {
		ghostConnectors,
		showGhostIcons,
		isGhostIconsPending,
		shouldAnimate,
		oauthSource: showGhostIcons ? 'ghostIconNudge' : 'appFilterMenu',
		handleSourcesPillClick,
		handleGhostConnectClick,
		reportRenderBlocked,
	};
};
